import React, { useState, useEffect } from 'react';
import { Card, Collapse, Row, Col, Select, Input, Button, Flex, Image, Upload, List, message } from 'antd';
import axios from '../util/axiosConfig';
import { useLocation } from 'react-router-dom';
import LineItemEditTable from './Tables/LineItemEditTable';
import '../styles/reports.css';
import { useInvContext } from '../context/InvContext';
import ViewUpload from './ViewUpload';
import invoiceImage from '../assets/images/invoiceGRNicon.svg';
import { UploadOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";

const { Option } = Select;

const SingleInvoiceEdit: React.FC = () => {
    const { isAdmin } = useInvContext();
    const location = useLocation();
    const panelData = location.state;
    const [disputeReasons, setDisputeReasons] = useState<string[]>([]);
    const [isEditable, setIsEditable] = useState(false);
    const [isUpdateButton, setUpdateButton] = useState(false);
    const [isShowRemarks, setIsShowRemarks] = useState(false);
    const [selectedDisputeReason, setSelectedDisputeReason] = useState<number | undefined>(undefined);
    const [invoiceData, setInvoiceData] = useState<any>({});
    const [removeEdit, setRemoveEdit] = useState(false);
    const [isViewUpload, setIsViewUpload] = useState(false);
    const [files, setFiles] = useState<any[]>([]);
    const [invoiceDoc, setInvoiceDoc] = useState<any[]>([]);
    const [isSublineAdd, setIsSublineAdd] = useState(false);

    const fetchInvoiceData = async (id: number) => {
        try {
            const response = await axios.get(`/get-invoice-dto`, {
                params: {
                    invoiceId: id
                },
            });
            if (response?.data) {
                setInvoiceData(response?.data?.data);
                setRemoveEdit(response?.data?.data?.status.toLowerCase().startsWith('success'));
            }
        } catch (error) {
            console.error('Failed to fetch invoice data:', error);
        }
    };

    useEffect(() => {
        const fetchDisputeReasons = async () => {
            try {
                const response = await axios.get('/dispute-reason/list');
                if (response?.data?.data) {
                    setDisputeReasons(response.data.data);
                }
            } catch (error) {
                console.error('Failed to fetch dispute reasons:', error);
            }
        };

        if (panelData?.id) {
            fetchInvoiceData(panelData.id);
        }
        fetchDisputeReasons();
        // eslint-disable-next-line
    }, [panelData?.id]);

    const handleDisputeReasonChange = (value: any) => {
        setInvoiceDoc([]);
        setFiles([]);
        if (value === 5 || value === 6) {
            setIsEditable(true);
            setUpdateButton(true)
            setIsShowRemarks(false);
            setIsViewUpload(false);
            setIsSublineAdd(false);
        } else if([1, 2, 7, 8].includes(value) ) {
            setUpdateButton(true);
            setIsEditable(true);
            setIsShowRemarks(false);
            setIsViewUpload(true);
            setIsSublineAdd(true);
        }
        else if (value === 3) {
            setIsEditable(false);
            setUpdateButton(false);
            setIsShowRemarks(true);
            setIsViewUpload(false);
            setIsSublineAdd(false);
        } else {
            setIsEditable(false);
            setIsShowRemarks(false);
            setUpdateButton(false);
            setIsViewUpload(false);
            setIsSublineAdd(false);

        }
        setSelectedDisputeReason(value);
    };

    const handleFieldChange = (field: string, event: React.ChangeEvent<HTMLInputElement>) => {
        setInvoiceData((prevData: any) => ({
            ...prevData,
            [field]: event.target.value
        }));
    };

    const resetDisputeReason = async (finalRemarks: any) => {
        try {
            const payload = {
                invoicePageableDto: {
                    id: panelData?.id || 0,
                    reference: invoiceData.reference || '',
                    subject: invoiceData.subject || '',
                    invoiceReference: invoiceData.invoiceReference || '',
                    companyReference: invoiceData.companyReference || '',
                    currencyCode: invoiceData.currencyCode || '',
                    cfPostingDate: invoiceData.cfPostingDate || '',
                    invoiceDate: invoiceData.invoiceDate || '',
                    billingAddressReference: invoiceData.billingAddressReference || '',
                    subTotal: invoiceData.subTotal || 0,
                    poDate: invoiceData.poDate || '',
                    poNumber: invoiceData.poNumber || '',
                    status: invoiceData.status || '',
                    vendorName: invoiceData.vendorName || '',
                    taxableValue: invoiceData.taxableValue || 0,
                    taxAmount: parseFloat(invoiceData.taxAmount) || 0,
                    totalAmount: parseFloat(invoiceData.totalAmount) || 0,
                    fileDetailsId: invoiceData.fileDetailsId || 0,
                    s3Url: invoiceData.s3Url || '',
                    rejectionRemark: finalRemarks || invoiceData.rejectionRemark || '',
                    vendorUploadDtos: invoiceData?.vendorUploadDtos || []
                },
                lineItemPageableDtos: []
            };

            await axios.post('/update-invoice', payload);
            if (panelData?.id) {
                fetchInvoiceData(panelData.id);
            }
        } catch (error) {
            console.error('Failed to update invoice:', error);
        }

        setSelectedDisputeReason(undefined);
        handleDisputeReasonChange(null);
    };

    const modificationLogroute = (e: any, panelData: any) => {
        const queryString = new URLSearchParams(panelData).toString();
        window.open(`/modification-log?${queryString}`, '_blank');
    }

    const handleBeforeUpload = (file: File) => {
        handleFileUpload(file);
        return false; 
    };

    const handleFileUpload = async (file: File) => {
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await axios.post("/vendor-attachment-upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const fileData = response.data.data[0];
            const uploadedFile = {
                fileDetailId: fileData.fileDetailId,
                name: file.name,
                s3Url: fileData.s3Url,
            };

            setFiles((prevFiles) => [...prevFiles, uploadedFile]);
            setInvoiceDoc((prevDocs) => [...prevDocs, uploadedFile]);
            message.success(`${file.name} uploaded successfully.`);
        } catch (error) {
            console.error("File upload failed:", error);
            message.error(`File upload failed: ${file.name}`);
        }
    };

    const handleRemove = (fileDetailId: string) => {
        setFiles((prevFiles) => prevFiles.filter((file) => file.fileDetailId !== fileDetailId));
        setInvoiceDoc((prevDocs) => prevDocs.filter((doc) => doc.fileDetailId !== fileDetailId));
        message.success("File removed successfully.");
    };

    const handleView = (fileDetailId: string) => {
        const currentFile = files.find((file) => file.fileDetailId === fileDetailId);
        if (currentFile?.s3Url) {
            window.open(currentFile.s3Url, '_blank');
        }
    };

    const renderPanelHeader = () => (
        <>
            <Row>
            {isAdmin && !removeEdit ? (
                <Col span={24} className='align-center-div'>
                <Row className="header-list-row" style={{ width: '100%' }}>
                <Col span={7}>
                        <Row>
                            <Col span={3}>
                            <Image src={invoiceImage} alt="invoiceImage" className="invoiceLogoEditSec" preview={false} />
                            </Col>
                            <Col span={17}>
                            <div className='invoiceDetails'>
                            <p className='vendorName invLabels'>No: </p>
                            <Input className='invInputs'
                                value={invoiceData?.invoiceReference}
                                onChange={(e) => handleFieldChange('invoiceReference', e)}
                            />
                            </div>
                            <div className='invoiceDetails'>
                            <p className='vendorName invLabels'>Vendor: </p>
                              <Input className='invInputs' 
                                value={invoiceData?.vendorName || ''}
                                onChange={(e) => handleFieldChange('vendorName', e)}
                            />
                            </div>
                            </Col>
                           
                        </Row>
                    </Col>
                    {/* <Col span={3}>
                        <p className='panel-head-txt'>Invoice No</p>
                            <Input
                                value={invoiceData?.invoiceReference}
                                onChange={(e) => handleFieldChange('invoiceReference', e)}
                            />
                    </Col>
                    <Col span={4}>
                        <p className='panel-head-txt'>Vendor Name</p>
                            <Input
                                value={invoiceData?.vendorName || ''}
                                onChange={(e) => handleFieldChange('vendorName', e)}
                            />
                    </Col> */}
                    <Col span={3}>
                        <p className='panel-head-txt'>PO Number</p>
                            <Input
                                value={invoiceData?.poNumber || ''}
                                onChange={(e) => handleFieldChange('poNumber', e)}
                            />
                    </Col>
                    <Col span={2}>
                        <p className='panel-head-txt'>Tax Amount</p>
                            <Input
                                value={invoiceData?.taxAmount || ''}
                                onChange={(e) => handleFieldChange('taxAmount', e)}
                            />
                    </Col>
                    <Col span={3}>
                        <p className='panel-head-txt'>Total Amount</p>
                            <Input
                                value={invoiceData?.totalAmount || ''}
                                onChange={(e) => handleFieldChange('totalAmount', e)}
                            />
                    </Col>
                    <Col span={4}>
                        <p className='panel-head-txt'>Status</p>
                        <p
                            className={typeof invoiceData?.status === 'string' && invoiceData?.status.toLowerCase().startsWith('success') ? 'greenStatus' :
                                invoiceData?.status === '' ? 'blueStatus' : 'redStatus'}
                        >
                            {invoiceData?.status ? invoiceData.status : "Send to SAP"}
                        </p>
                    </Col>
                    <Col span={3}>
                        <p className='panel-head-txt'>Dispute Reason</p>
                        <Select
                            placeholder="Select a reason"
                            onChange={handleDisputeReasonChange}
                            value={selectedDisputeReason}
                            style={{ width: '100%' }}
                            disabled={removeEdit}
                        >
                            {disputeReasons.map((reason: any) => (
                                <Option key={reason?.id} value={reason?.id}>
                                    {reason?.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
            </Col>
            ) : (<Col span={24} className='align-center-div'>
                <Row className="header-list-row" style={{ width: '100%' }}>
                    <Col span={7}>
                        <Row>
                            <Col span={3}>
                            <Image src={invoiceImage} alt="invoiceImage" className="invoiceLogo" preview={false} />
                            </Col>
                            <Col span={17}><p className='invoiceName'>{invoiceData?.invoiceReference || '-'}</p>
                            <p className='vendorName'>Vendor: {invoiceData?.vendorName || '-'}</p>
                            </Col>
                           
                        </Row>
                    </Col>
                    <Col span={3}>
                        <p className='invoiceLabels'>PO No:</p>
                        <p className='invoiceTxtvalue'>{invoiceData?.poNumber || '-'}</p>
                    </Col>
                    <Col span={2}>
                        <p className='invoiceLabels'>Tax Amount:</p>
                        {isAdmin && !removeEdit ? (
                            <Input
                                value={invoiceData?.taxAmount || ''}
                                onChange={(e) => handleFieldChange('taxAmount', e)}
                            />
                        ) : (
                            <p className='invoiceTxtvalue'>{invoiceData?.taxAmount || '-'}</p>
                        )}
                    </Col>
                    <Col span={3}>
                        <p className='invoiceLabels'>Total Amount:</p>
                        {isAdmin && !removeEdit ? (
                            <Input
                                value={invoiceData?.totalAmount || ''}
                                onChange={(e) => handleFieldChange('totalAmount', e)}
                            />
                        ) : (
                            <p className='invoiceTxtvalue'>{invoiceData?.totalAmount || '-'}</p>
                        )}
                    </Col>
                    <Col span={4}>
                        <p className='invoiceLabels'>Status:</p>
                        <p
                            className={typeof invoiceData?.status === 'string' && invoiceData?.status.toLowerCase().startsWith('success') ? 'greenStatus' :
                                invoiceData?.status === '' ? 'blueStatus' : 'redStatus'}
                        >
                            {invoiceData?.status ? invoiceData.status : "Send to SAP"}
                        </p>
                    </Col>
                    <Col span={3}>
                        <p className='invoiceLabels'>Dispute Reason:</p>
                        <Select
                            placeholder="Select a reason"
                            onChange={handleDisputeReasonChange}
                            value={selectedDisputeReason}
                            style={{ width: '100%' }}
                            disabled={removeEdit}
                        >
                            {disputeReasons.map((reason: any) => (
                                <Option key={reason?.id} value={reason?.id}>
                                    {reason?.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
            </Col>)
            }
            </Row>
            {( isViewUpload &&
                <Row>
                    <Col span={24}>
                        <div>
                        <h3>Upload Documents</h3>
                            <Upload
                                multiple
                                beforeUpload={handleBeforeUpload}
                                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                                showUploadList={false}
                            >
                                <Button icon={<UploadOutlined />}>Click to Upload</Button>
                            </Upload>
                            <br />
                            <List
                                bordered
                                dataSource={files}
                                renderItem={(file) => (
                                    <List.Item
                                        actions={[
                                            <Button
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                danger
                                                onClick={() => handleRemove(file.fileDetailId)}
                                            >
                                                Remove
                                            </Button>,
                                            <Button
                                                type="text"
                                                icon={<EyeOutlined />}
                                                danger
                                                onClick={() => handleView(file.fileDetailId)}
                                            >
                                                View
                                            </Button>,
                                        ]}
                                    >
                                        {file.name}
                                    </List.Item>
                                )}
                            />
                        </div>
                    </Col>
                </Row>
            )}
            {invoiceData?.vendorUploadDtos?.length > 0 && (
                <Row>
                    <Col span={24}>
                        <Flex align='center' justify='flex-start'>
                            <ViewUpload fileList={invoiceData?.vendorUploadDtos} />
                        </Flex>
                    </Col>
                </Row>
            )}
        </>
    );

    return (
        <Card bordered={false} title={'Edit - Invoice'} className='editPageContainer'>
            <Button className='modLog' onClick={(e) => modificationLogroute(e, panelData)}>Modification Log</Button>
            <Collapse
                activeKey={panelData?.id}
                bordered={false}
                accordion={true}
                className='mt-2 single-edit-collapse'
            >
                <Collapse.Panel
                    header={renderPanelHeader()}
                    key={panelData?.id.toString()}
                >
                    <div>
                        <LineItemEditTable
                            panelID={panelData?.id.toString()}
                            rejectionRemark={invoiceData.rejectionRemark}
                            isEdit={isEditable}
                            isRemarks={isShowRemarks}
                            disputeReasonID={selectedDisputeReason}
                            isLineItemChange={resetDisputeReason}
                            updatable={isUpdateButton}
                            fileDetailsIdList ={invoiceDoc}
                            isSublineAdd={isSublineAdd}
                            isSuccess={invoiceData?.status?.toLowerCase().startsWith('success')}
                        />
                    </div>
                </Collapse.Panel>
            </Collapse>
        </Card>
    );
};

export default SingleInvoiceEdit;
